/**
 * SuperAdmin → Resumen. Todo en vivo: qué cuentas y empresas hay, cuánto se factura,
 * qué requiere atención (certificados, Hacienda, respaldos, planes) y la salud de cada cuenta.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2, Users, FileText, Banknote, HardDrive, RefreshCw, ShieldAlert, CheckCircle2, ChevronRight, BarChart3, ListChecks
} from 'lucide-react';
import SuperadminLayout from '../components/SuperadminLayout';
import { panelApi } from '../utils/api';
import {
  Encabezado, Panel, Kpi, Boton, Cargando, Aviso, Salud, EstadoCuenta, Severidad, BarrasDiarias,
  fNum, fColones, fBytes, hace, fFechaHora
} from '../components/ui';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [refrescando, setRefrescando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [verTodas, setVerTodas] = useState(false);
  const navigate = useNavigate();

  const cargar = useCallback(async (forzar = false) => {
    forzar ? setRefrescando(true) : setCargando(true);
    try {
      const r = await panelApi.resumen(forzar);
      setData(r.data.data);
    } catch (e) {
      setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudo cargar el resumen' });
    } finally {
      setCargando(false);
      setRefrescando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const k = data?.kpis;
  const alertas = data?.alertas || [];
  const visibles = verTodas ? alertas : alertas.slice(0, 8);
  const criticas = alertas.filter(a => a.severidad === 'critica').length;
  const serie = (data?.serie || []).map(d => ({ fecha: d.fecha, valor: d.comprobantes }));
  const total30 = serie.reduce((s, d) => s + d.valor, 0);

  return (
    <SuperadminLayout>
      <Encabezado titulo="Resumen" subtitulo={data ? `Datos en vivo de todas las cuentas · actualizado ${hace(data.generado)}` : 'Datos en vivo de todas las cuentas'}>
        <Boton icono={RefreshCw} cargando={refrescando} onClick={() => cargar(true)}>Actualizar</Boton>
      </Encabezado>
      <Aviso aviso={aviso} onClose={() => setAviso(null)} />

      {cargando ? <Cargando texto="Consultando las bases de cada cuenta…" /> : data && (
        <div className="space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
            <Kpi icono={Building2} color="blue" titulo="Cuentas activas" valor={fNum(k.cuentas.activas)}
              detalle={`${fNum(k.empresas)} empresas${k.cuentas.suspendidas ? ` · ${k.cuentas.suspendidas} suspendida(s)` : ''}`} />
            <Kpi icono={Users} color="purple" titulo="Usuarios activos" valor={fNum(k.usuarios.activos)}
              detalle={`${fNum(k.usuarios.activos30d)} ingresaron en 30 días`} />
            <Kpi icono={FileText} color="green" titulo="Comprobantes este mes" valor={fNum(k.comprobantesMes)}
              detalle={`${fNum(total30)} en los últimos 30 días`} />
            <Kpi icono={Banknote} color="amber" titulo="Facturado este mes" valor={fColones(k.facturadoMesCRC, true)}
              detalle="Aceptado por Hacienda, neto de NC" />
            <Kpi icono={HardDrive} color="slate" titulo="Datos" valor={fBytes(k.baseBytes)}
              detalle={`${fBytes(k.respaldoBytes)} en respaldos`} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            {/* Requiere atención */}
            <Panel className="xl:col-span-3" titulo="Requiere atención" icono={ShieldAlert} sinPadding
              accion={alertas.length > 0 && (
                <span className="text-xs text-gray-500">{criticas ? <span className="text-red-600 font-medium">{criticas} crítico(s)</span> : null}{criticas ? ' · ' : ''}{alertas.length} en total</span>
              )}>
              {alertas.length === 0 ? (
                <div className="flex items-center gap-3 px-5 py-8 text-emerald-700">
                  <CheckCircle2 className="h-6 w-6" />
                  <div>
                    <p className="font-medium">Todo en orden</p>
                    <p className="text-sm text-gray-500">Certificados vigentes, sin rechazos ni comprobantes trabados, respaldos al día.</p>
                  </div>
                </div>
              ) : (
                <>
                  <ul className="divide-y divide-gray-100">
                    {visibles.map((a, i) => (
                      <li key={i}>
                        <button onClick={() => navigate(a.tipo === 'hacienda' ? '/superadmin/rechazos' : a.tipo === 'esquema' ? '/superadmin/respaldos' : `/superadmin/cuentas/${a.cuentaId}`)}
                          className="w-full text-left flex items-start gap-3 px-5 py-3 hover:bg-gray-50">
                          <Severidad nivel={a.severidad} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900">{a.texto}</p>
                            <p className="text-xs text-gray-500 truncate">{a.cuenta}{a.empresa ? ` · ${a.empresa}` : ''}</p>
                          </div>
                          <ChevronRight className="h-4 w-4 text-gray-300 mt-0.5 shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                  {alertas.length > 8 && (
                    <button onClick={() => setVerTodas(!verTodas)} className="w-full py-2.5 text-sm text-blue-600 hover:bg-gray-50 border-t border-gray-100">
                      {verTodas ? 'Ver menos' : `Ver las ${alertas.length}`}
                    </button>
                  )}
                </>
              )}
            </Panel>

            {/* Comprobantes por día */}
            <Panel className="xl:col-span-2" titulo="Comprobantes por día · últimos 30 días" icono={BarChart3}>
              <BarrasDiarias datos={serie} />
              <p className="text-xs text-gray-500 mt-3">FE, TE, ND y NC enviados a Hacienda, de todas las cuentas.</p>
            </Panel>
          </div>

          {/* Salud por cuenta */}
          <Panel titulo="Cuentas" icono={ListChecks} sinPadding
            accion={<Link to="/superadmin/cuentas" className="text-sm text-blue-600 hover:underline">Administrar cuentas</Link>}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left font-medium px-5 py-2.5">Cuenta</th>
                    <th className="text-left font-medium px-3 py-2.5">Salud</th>
                    <th className="text-right font-medium px-3 py-2.5">Empresas</th>
                    <th className="text-right font-medium px-3 py-2.5">Usuarios</th>
                    <th className="text-right font-medium px-3 py-2.5">Comprobantes mes</th>
                    <th className="text-right font-medium px-3 py-2.5">Facturado mes</th>
                    <th className="text-left font-medium px-3 py-2.5">Último comprobante</th>
                    <th className="text-left font-medium px-3 py-2.5">Último respaldo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.cuentas.map(c => (
                    <tr key={c.id} onClick={() => navigate(`/superadmin/cuentas/${c.id}`)} className="cursor-pointer hover:bg-gray-50">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{c.nombre}</span>
                          {c.estado !== 'active' && <EstadoCuenta estado={c.estado} />}
                        </div>
                        <span className="text-xs text-gray-500">{c.slug} · plan {c.plan}</span>
                      </td>
                      <td className="px-3 py-3"><Salud alertas={c.alertas} error={c.error} /></td>
                      <td className="px-3 py-3 text-right text-gray-700">{c.empresas}</td>
                      <td className="px-3 py-3 text-right text-gray-700">{c.usuarios.activos}</td>
                      <td className="px-3 py-3 text-right text-gray-900 font-medium">{fNum(c.comprobantesMes)}</td>
                      <td className="px-3 py-3 text-right text-gray-900">{fColones(c.facturadoMesCRC, true)}</td>
                      <td className="px-3 py-3 text-gray-600" title={fFechaHora(c.ultimoComprobante)}>{hace(c.ultimoComprobante)}</td>
                      <td className="px-3 py-3 text-gray-600" title={fFechaHora(c.respaldo?.ultimoExitoso)}>
                        {c.respaldo?.ultimoExitoso ? hace(c.respaldo.ultimoExitoso) : <span className="text-amber-700">nunca</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}
    </SuperadminLayout>
  );
}
